import React, { useState } from 'react';
import { Search, Play, Clock, Eye } from 'lucide-react';
import { Card, CardMedia } from '../components/ds/Card';
import { Badge } from '../components/ds/Badge';
import { PageHero } from '../components/ds/theme';
import worshipBg from '../assets/images/worship_bg.jpg';
import prayer2 from '../assets/images/prayer2.jpg';
import prayerBg from '../assets/images/prayer_bg.jpg';
import sundaySchoolBg from '../assets/images/sunday_school_bg.jpg';
import bibleStudyBg from '../assets/images/bible_study_bg.jpg';
import ministrytile from '../assets/images/ministrytile.jpg';

const SERMONS = [
  { date: 'February 16, 2026', title: 'Walking in Faith, Not by Sight', speaker: 'Pastor William S. Coleman', img: worshipBg, time: '42 min', views: 127, series: 'New' },
  { date: 'February 9, 2026', title: 'The Power of Prayer', speaker: 'Pastor William S. Coleman', img: prayer2, time: '38 min', views: 203 },
  { date: 'February 2, 2026', title: 'Grace That Saves', speaker: 'Pastor William S. Coleman', img: prayerBg, time: '45 min', views: 189 },
  { date: 'January 26, 2026', title: 'Living in the Spirit', speaker: 'Rev. David Thompson', img: sundaySchoolBg, time: '41 min', views: 156 },
  { date: 'January 19, 2026', title: 'Love One Another', speaker: 'Pastor William S. Coleman', img: bibleStudyBg, time: '39 min', views: 178 },
  { date: 'January 12, 2026', title: 'Strength in Weakness', speaker: 'Pastor William S. Coleman', img: ministrytile, time: '44 min', views: 195 },
];

const FILTERS = ['All', 'Recent', 'Series', 'Pastor Coleman', 'Rev. Thompson'];

function Sermons() {
  const [filter, setFilter] = useState('All');

  const visible = SERMONS.filter((s) => {
    if (filter === 'All') return true;
    if (filter === 'Recent') return true; // already sorted desc
    if (filter === 'Series') return !!s.series;
    if (filter === 'Pastor Coleman') return s.speaker.includes('Coleman');
    if (filter === 'Rev. Thompson') return s.speaker.includes('Thompson');
    return true;
  });

  return (
    <main>
      <PageHero eyebrow="Watch & Listen" title="Sermons" subtitle="Messages of truth, hope, and transformation." image={bibleStudyBg} />

      <section style={{ background: 'var(--cream)', padding: '36px var(--gutter)', borderBottom: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '280px', position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--tan-600)' }} />
            <input
              placeholder="Search sermons by title or scripture…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '14px 18px 14px 44px',
                fontFamily: 'var(--font-ui)',
                fontSize: '16px',
                border: '2px solid var(--tan-200)',
                borderRadius: 'var(--radius-sm)',
                outline: 'none',
                background: '#fff',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'var(--burgundy-700)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--tan-200)'; }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: '11px 20px',
                fontFamily: 'var(--font-ui)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
                border: '2px solid',
                borderColor: filter === f ? 'var(--burgundy-700)' : 'var(--tan-200)',
                background: filter === f ? 'var(--burgundy-700)' : '#fff',
                color: filter === f ? '#fff' : 'var(--text-strong)',
                transition: 'all var(--dur) var(--ease)',
              }}>{f}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="ds-section-y" style={{ background: 'var(--white)', padding: 'var(--section-y-sm) var(--gutter)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '28px' }}>
          {visible.map((s) => (
            <Card key={s.title} accent="burgundy" media={
              <div style={{ position: 'relative' }}>
                <CardMedia image={s.img} height="200px" />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(79,5,5,0.6), transparent 55%)' }} />
                <div style={{ position: 'absolute', left: '16px', bottom: '14px', width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--burgundy-700)' }}>
                  <Play size={22} style={{ marginLeft: '2px' }} />
                </div>
                {s.series && (
                  <div style={{ position: 'absolute', right: '14px', top: '14px' }}>
                    <Badge tone="burgundy">New Series</Badge>
                  </div>
                )}
              </div>
            }>
              <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--burgundy-700)', marginBottom: '8px' }}>{s.date}</div>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '24px', fontWeight: 500, color: 'var(--navy-800)', margin: '0 0 8px', lineHeight: 1.2 }}>{s.title}</h3>
              <div style={{ fontSize: '15px', color: 'var(--text-body)', marginBottom: '16px' }}>{s.speaker}</div>
              <div style={{ display: 'flex', gap: '18px', paddingTop: '14px', borderTop: '1px solid var(--line)', fontSize: '13.5px', color: 'var(--text-muted)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Clock size={15} /> {s.time}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Eye size={15} /> {s.views} views</span>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Sermons;
